import {
    FORM_ENTRIES_SET_MIGRATION_PANES,
    getFormEntry,
    isEmpty,
    NEW_TEMPLATE,
} from "@rpforms/shared";
import { user } from "@rpforms/shared/build/actions/actions_helper";
import { openEditor } from "@rpforms/shared/build/actions/editor";
import { Loader } from "@rpforms/shared/build/components/universal/Loader";
import PageNavAction from "@rpforms/shared/build/components/universal/PageNavAction";
import PageNavbar from "@rpforms/shared/build/components/universal/PageNavbar";
import { remapFormDataToSchema } from "@rpforms/shared/build/utilities/formDataMigrator";
import { findInFields } from "@rpforms/shared/build/utils";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Route, Switch } from "react-router-dom";
import styled from "styled-components";
import { MigrationPane } from "../../../components/migrations/MigrationPane";
import MigrationPaneActions from "../../../components/migrations/MigrationPaneActions";
import Migrator from "../../../components/migrations/Migrator";
import { API_URL, COMPANY_ID } from "../../../config";
import {
    IZugferdBtPayload,
    ModalBuyerOrderReference,
} from "../../../components/modal/ModalBuyerOrderReference";

export const AlignHorizontal = styled.div`
    display: flex;
`;

export const TemplateEntryPage = () => {
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const { sourceId } = useParams();
    const migrationPanes = useSelector<any, any>((state) => state.formEntries.migrationPanes);
    const [revertData, setRevertData] = useState(null);
    const [inited, setInited] = useState(false);
    const [showBuyerOrderModal, setShowBuyerOrderModal] = useState(false);
    const [buyerOrderReference, setBuyerOrderReference] = useState("");
    const [zugferdBtFields, setZugferdBtFields] = useState<any>({});

    const applyMigration = (sourceData, rawTargetSchema, rawSourceSchema) => {
        const migration = remapFormDataToSchema(sourceData, rawTargetSchema, rawSourceSchema);
        return migration.formData;
    };

    const addPane = (formEntry) => {
        dispatch({
            type: FORM_ENTRIES_SET_MIGRATION_PANES,
            payload: [...migrationPanes, formEntry],
        });
    };

    const setPanes = (formEntries) => {
        dispatch({
            type: FORM_ENTRIES_SET_MIGRATION_PANES,
            payload: formEntries,
        });
    };

    useEffect(() => {
        if (!inited) {
            // just do once
            if (!isEmpty(migrationPanes)) {
                setInited(true);
                // copyFormData(0, 1);
            }
        }
    }, [migrationPanes]);

    useEffect(() => {
        setBuyerOrderReference(migrationPanes[0]?.form_entry?.buyer_order_reference_number || "");
        setZugferdBtFields(migrationPanes[0]?.form_entry?.zugferd_bt_fields || {});
    }, [migrationPanes]);

    // preload required data
    useEffect(() => {
        const preload = async () => {
            if (sourceId) {
                dispatch(
                    getFormEntry(sourceId, (formEntryData, formValues) => {
                        const sourceFormDataStr =
                            formEntryData.latest_revision?.form_data ||
                            formEntryData.form_revision?.form_data;
                        const sourceFormData =
                            "object" === typeof sourceFormDataStr
                                ? sourceFormDataStr
                                : JSON.parse(sourceFormDataStr || "{}");
                        const nonApplyableFieldIds = getAllFieldsBy(
                            sourceFormData.fields,
                            true
                        ).map((field) => field.hashCode);
                        let overrideData = { ...formEntryData.form_data };
                        nonApplyableFieldIds.forEach((hc) => {
                            delete overrideData[hc];
                        });

                        overrideData = applyMigration(
                            overrideData,
                            formEntryData.latest_revision?.form_data ||
                                formEntryData.form_revision?.form_data,
                            sourceFormData
                        );
                        const panes = [
                            {
                                display: "none",
                                form_entry: { ...formEntryData },
                            },
                            {
                                form_entry: {
                                    ...formEntryData,
                                    id: null,
                                    form_data: overrideData,
                                    form_revision: { ...formEntryData.latest_revision },
                                    form_revision_id: formEntryData.latest_revision_id,
                                },
                                source_form_entry: { ...formEntryData },
                                isHidden: false,
                            },
                        ];
                        setPanes(panes);

                        const refreshInvoicePositions = async () => {
                            const rawSchema =
                                formEntryData.latest_revision?.form_data ||
                                formEntryData.form_revision?.form_data;
                            const schemaData =
                                typeof rawSchema === "string" ? JSON.parse(rawSchema) : rawSchema;
                            const invoiceField = findInFields(
                                schemaData?.fields || [],
                                (field) => field.type === "InvoicePositionsListField"
                            );

                            if (!invoiceField?.hashCode) {
                                return;
                            }

                            let invoiceData = overrideData[invoiceField.hashCode];
                            if (!invoiceData) {
                                return;
                            }

                            if (typeof invoiceData === "string") {
                                try {
                                    invoiceData = JSON.parse(invoiceData);
                                } catch (e) {
                                    return;
                                }
                            }

                            const invoicePositions = invoiceData.invoicePositions || [];
                            const inventoryItemIds = Array.from(
                                new Set(
                                    invoicePositions
                                        .map((position) => position.inventory_item_id)
                                        .map((id) => {
                                            if (typeof id === "string") {
                                                const parsed = parseInt(id, 10);
                                                return Number.isFinite(parsed) ? parsed : null;
                                            }
                                            if (typeof id === "number" && Number.isFinite(id)) {
                                                return id;
                                            }
                                            return null;
                                        })
                                        .filter((id): id is number => id !== null)
                                )
                            );

                            if (inventoryItemIds.length === 0) {
                                return;
                            }

                            try {
                                const idsQuery = inventoryItemIds
                                    .map((id) => `ids[]=${encodeURIComponent(String(id))}`)
                                    .join("&");
                                const response = await axios.get(
                                    `${API_URL}/companies/${COMPANY_ID}/inventory_items.json?${idsQuery}`,
                                    {
                                        headers: { authorization: user().token },
                                    }
                                );
                                const inventoryItems = (response?.data?.inventory_items ||
                                    []) as Array<any>;
                                const inventoryItemMap = new Map(
                                    inventoryItems.map((item) => [item.id, item])
                                );

                                const updatedPositions = invoicePositions.map((position) => {
                                    const inventoryId = position.inventory_item_id;
                                    if (!inventoryId) {
                                        return position;
                                    }

                                    const normalizedId =
                                        typeof inventoryId === "string"
                                            ? parseInt(inventoryId, 10)
                                            : inventoryId;
                                    const inventoryItem = inventoryItemMap.get(normalizedId) as any;
                                    if (!inventoryItem) {
                                        return { ...position, inventory_item_missing: true };
                                    }

                                    return {
                                        ...position,
                                        inventory_item_missing: false,
                                    };
                                });

                                const nextFormData = {
                                    ...overrideData,
                                    [invoiceField.hashCode]: {
                                        ...invoiceData,
                                        invoicePositions: updatedPositions,
                                    },
                                };

                                const updatedPanes = [
                                    panes[0],
                                    {
                                        ...panes[1],
                                        form_entry: {
                                            ...panes[1].form_entry,
                                            form_data: nextFormData,
                                        },
                                    },
                                ];
                                setPanes(updatedPanes);
                            } catch (e) {
                                console.error("Failed to refresh inventory items", e);
                            }
                        };

                        refreshInvoicePositions();
                    })
                );
            }
        };
        preload();
    }, [sourceId]);

    const setPaneFormData = (to, formData) => {
        const newPanes = migrationPanes.map((p, i) => {
            if (i === to) {
                return {
                    ...p,
                    form_entry: {
                        ...p.form_entry,
                        form_data: formData,
                    },
                    isHidden: true,
                };
            }
            return p;
        });
        const newPanesVisible = migrationPanes.map((p, i) => {
            if (i === to) {
                return {
                    ...p,
                    form_entry: {
                        ...p.form_entry,
                        form_data: formData,
                    },
                    isHidden: false,
                };
            }
            return p;
        });
        requestAnimationFrame(() => {
            setPanes(newPanes);
            setPanes(newPanesVisible);
        });
    };

    const revertCopyFormData = () => {
        setPaneFormData(1, { ...revertData });
        setRevertData(null);
    };

    const getAllFieldsBy = (fields, condition) => {
        const key = "nontemplatable";
        let results = [];
        (fields || []).forEach((field) => {
            if (field.options[key] === condition) {
                results.push(field);
            }
            results = results.concat(getAllFieldsBy(field.subfields, condition));
        });

        return results;
    };

    const copyFormData = (from, to) => {
        const sourceFormDataStr = migrationPanes[from].form_entry?.form_revision?.form_data;
        const sourceFormData =
            "object" === typeof sourceFormDataStr
                ? sourceFormDataStr
                : JSON.parse(sourceFormDataStr || "{}");
        const nonApplyableFieldIds = getAllFieldsBy(sourceFormData.fields, true).map(
            (field) => field.hashCode
        );
        setRevertData({ ...migrationPanes[to].form_entry.form_data });
        const overrideData = { ...migrationPanes[from].form_entry.form_data };
        nonApplyableFieldIds.forEach((hc) => {
            delete overrideData[hc];
        });

        const newFormData = {
            ...migrationPanes[to].form_entry.form_data,
            ...applyMigration(
                overrideData,
                migrationPanes[to].form_entry?.form_revision?.form_data,
                sourceFormData
            ),
        };
        setPaneFormData(to, newFormData);
    };

    const isInvoiceForm = () => {
        const targetPane = migrationPanes[1];
        return targetPane?.form_entry?.form?.name === "000_Rechnung";
    };

    const proceedWithTemplate = (orderReference?: string, btFields: any = {}) => {
        const referenceToUse = orderReference !== undefined ? orderReference : buyerOrderReference;
        const btFieldsToUse =
            Object.keys(btFields || {}).length > 0 ? btFields : zugferdBtFields || {};

        const updatedPanes = migrationPanes.map((pane, index) => {
            return {
                ...pane,
                form_entry: {
                    ...pane.form_entry,
                    buyer_order_reference_number: referenceToUse,
                    zugferd_bt_fields: btFieldsToUse,
                },
            };
        });

        dispatch(
            openEditor({
                endpoint: `migrate`,
                endpointCompany: false,
                preload: updatedPanes,
                type: NEW_TEMPLATE,
            })
        );
    };

    const openSubmitFormEntryModal = () => {
        if (isInvoiceForm()) {
            setShowBuyerOrderModal(true);
            return;
        }

        dispatch(
            openEditor({
                endpoint: `migrate`,
                endpointCompany: false,
                preload: migrationPanes,
                type: NEW_TEMPLATE,
            })
        );
    };

    const handleBuyerOrderSave = (payload: IZugferdBtPayload) => {
        setBuyerOrderReference(payload.buyer_order_reference_number || "");
        setZugferdBtFields(payload.zugferd_bt_fields || {});
        setShowBuyerOrderModal(false);
        proceedWithTemplate(
            payload.buyer_order_reference_number || "",
            payload.zugferd_bt_fields || {}
        );
    };

    const handleBuyerOrderClose = () => {
        setShowBuyerOrderModal(false);
    };

    return (
        <>
            <Switch>
                <Route>
                    <PageNavbar
                        title={`Editor`}
                        description={
                            "Hier werden Formulare dupliziert und auf Basis von diesen neu erstellt"
                        }
                        subtitle={"Migrieren"}
                    >
                        <PageNavAction
                            onClick={() => window.history.back()}
                            icon="chevron-left"
                            title="Zurück"
                        />
                        {!isLoading && (
                            <PageNavAction
                                onClick={openSubmitFormEntryModal}
                                success={true}
                                icon="save"
                                title="Speichern"
                            />
                        )}
                    </PageNavbar>
                </Route>
            </Switch>
            <Migrator>
                {loading && (
                    <div>
                        <Loader />
                    </div>
                )}
                {!loading &&
                    migrationPanes.map((pane: any, index) => (
                        <AlignHorizontal key={index} style={{ display: pane.display || "block" }}>
                            <MigrationPane migrationPaneIndex={index} />
                            <MigrationPaneActions
                                copyFormData={copyFormData}
                                undoCopyFormData={revertCopyFormData}
                                undoable={revertData != null}
                                index={index}
                                panes={migrationPanes}
                            />
                        </AlignHorizontal>
                    ))}
            </Migrator>

            <ModalBuyerOrderReference
                entry={migrationPanes[1]?.form_entry}
                show={showBuyerOrderModal}
                onSave={handleBuyerOrderSave}
                onClose={handleBuyerOrderClose}
            />
        </>
    );
};

export default TemplateEntryPage;
